import {
  useDeleteCmsFavicon,
  useGetCmsFavicon,
  useUploadCmsFavicon,
} from "@/api/cms";
import { toast } from "@/hooks/use-toast";
import { resampleFileToMaxDimension } from "@/lib/canvas";
import { cn } from "@/lib/utils";
import { Loader2, Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/** Carré compact, même bordure/rayon que CertificateLogoUpload */
const FAVICON_BOX_CLASS =
  "relative size-28 shrink-0 overflow-hidden rounded-xl border border-slate-300 bg-white";

/** Limite appliquée par l'API (`validateCmsFavicon`). */
const MAX_FAVICON_DIMENSION = 512;

const isIcoFile = (file: File) =>
  file.type.includes("icon") || file.name.toLowerCase().endsWith(".ico");

const CmsFaviconUpload = () => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { data, isLoading: isLoadingFavicon } = useGetCmsFavicon();
  const { mutate: uploadFavicon, isPending: isUploading } =
    useUploadCmsFavicon();
  const { mutate: deleteFavicon, isPending: isDeleting } = useDeleteCmsFavicon();
  const [isResampling, setIsResampling] = useState(false);

  const isProcessing = isUploading || isDeleting || isResampling;
  const hasFavicon = Boolean(data?.favicon);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;

    // L'API rejette au-delà de 512 px : on redimensionne à la volée plutôt que
    // de renvoyer l'utilisateur vers un outil externe. Les .ico sont laissés
    // tels quels (l'API ne contrôle pas leurs dimensions).
    let fileToUpload = file;
    if (!isIcoFile(file)) {
      try {
        setIsResampling(true);
        const resampled = await resampleFileToMaxDimension(
          file,
          MAX_FAVICON_DIMENSION,
        );
        fileToUpload = resampled.file;
        if (resampled.resized) {
          toast({
            title: t("cms.generalSettings.favicon.autoResizedTitle", {
              defaultValue:
                "Favicon redimensionné (cms.generalSettings.favicon.autoResizedTitle)",
            }),
            description: t("cms.generalSettings.favicon.autoResized", {
              width: resampled.width,
              height: resampled.height,
              defaultValue: `Image ramenée à ${resampled.width} × ${resampled.height} px (cms.generalSettings.favicon.autoResized)`,
            }),
            duration: 3000,
          });
        }
      } catch {
        // Image illisible par le canvas : on laisse l'API trancher.
        fileToUpload = file;
      } finally {
        setIsResampling(false);
      }
    }

    uploadFavicon(fileToUpload, {
      onError: () => {
        toast({
          title: t("settingsErrorToastTitle"),
          description: t("settingsErrorToastDescription"),
          duration: 3000,
          variant: "destructive",
        });
      },
    });
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    deleteFavicon(undefined, {
      onSuccess: () => {
        toast({
          title: t("success"),
          description: t("homepageSuccessMessage"),
          duration: 1000,
          variant: "success",
        });
      },
      onError: () => {
        toast({
          title: t("settingsErrorToastTitle"),
          description: t("settingsErrorToastDescription"),
          duration: 3000,
          variant: "destructive",
        });
      },
    });
  };

  if (isLoadingFavicon) {
    return (
      <div
        className={cn(
          FAVICON_BOX_CLASS,
          "flex items-center justify-center bg-slate-50",
        )}
      >
        <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (hasFavicon && data?.faviconUrl) {
    return (
      <div className={FAVICON_BOX_CLASS}>
        <img
          key={data.faviconUrl}
          src={data.faviconUrl}
          alt={t("cms.generalSettings.favicon.title")}
          className="size-full object-contain p-3"
        />
        <button
          type="button"
          onClick={handleDelete}
          disabled={isProcessing}
          title={t("delete")}
          aria-label={t("delete")}
          className="absolute right-1.5 top-1.5 text-gray-400 transition-colors hover:text-red-500 disabled:opacity-50"
        >
          {isDeleting ? (
            <Loader2 className="h-5 w-5 animate-spin text-red-500" />
          ) : (
            <Trash2 className="h-5 w-5" />
          )}
        </button>
      </div>
    );
  }

  return (
    <label
      className={cn(
        FAVICON_BOX_CLASS,
        "flex cursor-pointer items-center justify-center hover:bg-slate-50 transition-colors",
        isProcessing && "pointer-events-none opacity-50",
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.ico,image/png,image/jpeg,image/x-icon"
        onChange={handleFileChange}
        className="absolute inset-0 size-full cursor-pointer opacity-0"
        aria-label={t("cms.generalSettings.favicon.clickCta")}
      />
      {isUploading || isResampling ? (
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      ) : (
        <Upload className="h-8 w-8 text-slate-400" />
      )}
    </label>
  );
};

export default CmsFaviconUpload;
